import React, {useEffect, useRef} from "react";
import { TextField } from "@mui/material";
import { ArrowBackIosNew } from "@mui/icons-material";
import { GridSaveAltIcon } from "@mui/x-data-grid";
import * as Yup from "yup";
import { useFormik } from "formik";
import {Button, Modal, Grid, Stack, Typography, ModalClose, Sheet, Card, Chip} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IWorkContact from "../../../types/work-contact.type";
import IServiceOrderAttachment from "../../../types/service-order-attachment.type";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import IconButton from "@mui/joy/IconButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface ModalProps {
  isOpen: boolean,
  onSubmit: (data: IServiceOrderAttachment) => void,
  onClose: () => void
}

const AttachmentModal: React.FC<ModalProps> = (props) => {
  const { isOpen, onClose, onSubmit} = props;

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const initialValues: IServiceOrderAttachment = {
    id: null,
    attachedAt: null,
    attachedBy: null,
    attachment: null,
    description: "",
    uploadAttachment: null
  };

  const validationSchema = Yup.object({
    description: Yup.string().required('La descripcion es requerida.'),
    uploadAttachment: Yup.mixed<File>()
      .nullable()
      .required("Ajunte un documento o archivo.")
      .test(
        "fileSize",
        "El archivo debe ser menor de 5 MB",
        (value) => !value || value.size <= 5 * 1024 * 1024
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: onSubmit,
    validationSchema: validationSchema,
  });

  useEffect(() => {
    formik.resetForm();
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Sheet
        variant="outlined"
        sx={{
          width: 730,
          borderRadius: 'md',
          p: 3,
          boxShadow: 'lg',
          maxHeight: 700,
          // overflow: "scroll"
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} />
        <Typography level="h4" component="h1" sx={{ paddingLeft: 1 }}>Adjuntar Nuevo Documento</Typography>
        <form className="form" onSubmit={formik.handleSubmit}>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={12} md={12} sm={12} xs={12}>
              <Card sx={{ height: 57, padding: 1, margin: 0 }}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack direction={"row"} justifyContent={"center"}>
                    <Typography sx={{ fontWeight: "bold" }} pt={1} pr={1}>Documentacion:</Typography>
                    {formik.values.uploadAttachment ? (
                      <Chip variant="outlined" color="neutral" size="lg">
                        {formik.values.uploadAttachment.name}
                      </Chip>
                    ) : (
                      <Typography pt={1} pr={1}>No hay archivo adjunto</Typography>
                    )}
                  </Stack>

                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <input
                      id={`uploadAttachment`}
                      ref={fileInputRef}
                      name="uploadAttachment"
                      type="file"
                      accept=".pdf,application/pdf"
                      style={{ display: "none" }}
                      onChange={(event) => {
                        if (event.currentTarget.files && event.currentTarget.files.length > 0) {
                          const newFile = event.currentTarget.files[0];
                          formik.setFieldValue("uploadAttachment", newFile);
                        }
                      }}
                    />

                    <IconButton onClick={() => fileInputRef.current?.click()}>
                      <CloudUploadIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Card>
              {formik.touched.uploadAttachment && formik.errors.uploadAttachment ? (
                <span className="Error-alert">{formik.errors.uploadAttachment}</span>
              ) : null}
            </Grid>

            <Grid xl={12} md={12} sm={12} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="description"
                  label="Descripcion"
                  name="description"
                  variant="filled"
                  type={"description"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                  error={!!(formik.touched.description && formik.errors.description)}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
                {formik.touched.description && formik.errors.description ? (
                  <span className="Error-alert">{formik.errors.description}</span>
                ) : null}
              </CssVarsProvider>
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{paddingRight: 1}}>
            <Button variant="outlined" endDecorator={<ArrowBackIosNew />} onClick={onClose}>Cerrar</Button>
            <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
          </Stack>
        </form>
      </Sheet>
    </Modal>
  );
};

export default AttachmentModal;
